import { CookieManager } from './CookieManager';
import { headerConstants } from './AuthConstants';
import { HeaderParser } from './dsAuth/parser/HeaderParser';
import Utils from '../utils/Utils';
import {
	ResponseToNative,
	HeadersJson,
	AuthenticationMode,
	ConstructAMLSecondaryTokenResponseFunctionParams,
	SecondaryTokenResponseToNative,
} from './dsAuth/data/AuthTypes';
import { ConstructAMLResponseFunctionType } from './dsAuth/data/AuthTypes';
import { parse } from 'cookie';
import { AuthChallenge, AuthChallengeConstants } from './dsAuth/data/AuthChallenge';
import { Logger } from './Logger';
import { logConstants } from '../utils/Constants';
import TokenManager from './TokenManager';
import { authErrorString } from './AuthErrors';
import OIDC from './oidc/OIDC';
import AuthMan from './AuthMan';
import { OIDCTokensTableKey, OIDCEndpointsTableKey } from './oidc/TypeDefinitions';
import OIDCEndpointsManager from './oidc/OIDCEndpointsManager';
import OIDCTokenManager from './oidc/OIDCTokenManager';
import { SentryLogger } from '../sentryLogging/SentryLogger';
import { StringMap } from '../utils/TypeDefinitions';
import * as Sentry from '@sentry/browser';
import { config } from './AuthConfig';
import { SingleStoreConfiguration } from 'src/Common/TypeDefinitions';
export default class AuthUtils {
	private static instance: AuthUtils;

	/// <summary>
	/// Create a new instance of AuthUtils if not already present.
	/// </summary>
	static getInstance(): AuthUtils {
		if (!AuthUtils.instance) {
			AuthUtils.instance = new AuthUtils();
		}
		return AuthUtils.instance;
	}

	/// <summary>
	/// get the auth domain from realm
	/// example: realm - "1.StoreFrontProdRp1.r_IWmK3yRkCNifZtUyILpQ.token"
	///			 auth domain - "r_IWmK3yRkCNifZtUyILpQ"
	/// </summary>
	getAuthDomainFromRealm(realm: string): string {
		const realmComponents = realm.split('.');
		let authDomain;
		if (realmComponents.length > 2) {
			authDomain = realmComponents[2];
		} else {
			authDomain = realm;
		}
		return authDomain;
	}

	/// <summary>
	/// get auth domain from request header
	/// </summary>
	getAuthDomainFromRequest(url: string, headers: HeadersJson): string {
		let authDomain = this.getAuthDomainFromURL(url);
		if (authDomain) {
			return authDomain;
		}
		authDomain = HeaderParser.getInstance().parseAuthDomainHeader(headers);
		return authDomain;
	}

	/// <summary>
	/// get auth domain from url (query param 'AD' or 'authDomain' (case insensitive lookup))
	/// </summary>
	getAuthDomainFromURL(url: string): string {
		const regExSearchForAD = /(ad)=/i.exec(url); //modifier i used to do case insensitive search
		const urlSearchParams = Utils.getInstance().getSearchParams(url);
		let authDomain;
		if (regExSearchForAD) {
			authDomain = urlSearchParams.get(regExSearchForAD[1]); //Position one gives paranthesised part from regEx , i.e AD here
		}
		if (!authDomain) {
			const regExSearchForAuthDomain = /(authDomain)=/i.exec(url);
			if (regExSearchForAuthDomain)
				authDomain = urlSearchParams.get(regExSearchForAuthDomain[1]);
		}
		return authDomain;
	}

	/// <summary>
	/// get auth domain from tokenServiceUrl (<hostname>/<authdomain>/...))
	/// </summary>
	getAuthDomainFromTokenServiceUrl(tokenServiceUrl: string): string {
		if (!!tokenServiceUrl) {
			try {
				return new URL(tokenServiceUrl).pathname.split('/')[1];
			} catch (e) {
				Logger.getInstance().write(logConstants.ERROR, `Invalid Url: ${tokenServiceUrl}`);
				const breadcrumb = {
					category: 'AuthUtils',
					message: `${e.message} : Invalid Url: ${tokenServiceUrl}`,
					level: Sentry.Severity.Error,
				};
				SentryLogger.getInstance().addBreadCrumb(breadcrumb);
			}
		}
		return '';
	}

	async addCookiesToRequestHeader(
		header: HeadersJson,
		url: string,
		referenceUri: string
	): Promise<HeadersJson> {
		const hdrs = JSON.parse(JSON.stringify(header));
		const cookieDetails = await CookieManager.getInstance().getAllCookiesForURL(
			url,
			referenceUri
		);
		let headerCookiesObj: StringMap = {};
		if (hdrs['Cookie']) {
			headerCookiesObj = parse(hdrs['Cookie']);
			hdrs['Cookie'] = '';
		}

		if (cookieDetails && Array.isArray(cookieDetails)) {
			for (const cookie of cookieDetails) {
				headerCookiesObj[cookie.name] = cookie.value;
			}
		}

		for (const [key, value] of Object.entries(headerCookiesObj)) {
			hdrs['Cookie'] = hdrs['Cookie']
				? hdrs['Cookie'] + '; ' + key + '=' + value
				: key + '=' + value;
		}
		return hdrs;
	}

	addTokenToRequestHeader(
		header: HeadersJson,
		token: string,
		mode: AuthenticationMode
	): HeadersJson {
		const scheme =
			mode === AuthenticationMode.DSAuth
				? headerConstants.CITRIX_AUTH_SCHEME
				: headerConstants.BEARER;
		header[headerConstants.AUTHORIZATION_HEADER] = scheme + ' ' + token;
		return header;
	}

	addLabelAndCredTypesToRequestHeader(
		header: HeadersJson,
		isOnPrem = false
	): HeadersJson {
		header[headerConstants.AM_LABEL_TYPE_HEADER] = headerConstants.AM_LABEL_TYPES;
		header[headerConstants.AM_CREDENTIAL_TYPE_HEADER] =
			isOnPrem || config.CredentialInsertionEnabled
				? headerConstants.AM_ONPREM_CREDENTIAL_TYPES
				: headerConstants.AM_CREDENTIAL_TYPES;
		return header;
	}

	constructAMLResponse(
		responseParams: ConstructAMLResponseFunctionType
	): ResponseToNative {
		return {
			id: responseParams.id,
			URLResponse: responseParams.URLResponse ? responseParams.URLResponse : null,
			token: responseParams.token ? responseParams.token : null,
			errorCode: responseParams.errorCode ? responseParams.errorCode : null,
			errorMessage: responseParams.errorMessage ? responseParams.errorMessage : null,
		};
	}

	constructAMLSecondaryTokenResponseToNative(
		responseParams: ConstructAMLSecondaryTokenResponseFunctionParams
	): SecondaryTokenResponseToNative {
		return {
			id: responseParams.id,
			token: responseParams.token ? responseParams.token : null,
			errorCode: responseParams.errorCode ? responseParams.errorCode : null,
			errorMessage: responseParams.errorMessage ? responseParams.errorMessage : null,
		};
	}
	/// <summary>
	/// format the realm with origin as prefix
	/// this is done since token can be reused only if the realm and the audience are same (audience includes the scheme and authority (hostname + port) from the Url which is nothing but the origin)
	/// </summary>
	/// <param name="realm">realm</param>
	/// <param name="origin">origin</param>
	/// <returns>formatted realm</returns>
	formatRealmForService(realm: string, origin: string): string {
		return origin + '/' + realm;
	}

	/**
	 * Takes action based on the reason in 401 challenge
	 *
	 * @param {AuthChallenge} authChallenge
	 * @param {string} referenceUri
	 * @param {string} id
	 * @param {boolean} isPrimaryTokenChallenge
	 * @param {string} [requestUrl] - optional param to be passed for secondary token challenge
	 * @memberof AuthUtils
	 */
	async takeActionForAuthChallenge(
		authChallenge: AuthChallenge,
		referenceUri: string,
		id: string,
		transactionId: string,
		isPrimaryTokenChallenge: boolean,
		requestUrl?: string
	): Promise<void> {
		try {
			Logger.getInstance().write(
				logConstants.INFO,
				id + ':AuthChallenge reason:' + authChallenge.getReason()
			);
			const authChallengeType = authChallenge.challengeType();
			switch (authChallengeType) {
				case AuthChallengeConstants.ACTION_NIL: // just continue with the flow
					break;
				case AuthChallengeConstants.DISCARD_TOKEN: // delete the primary or secondary token
					Logger.getInstance().write(
						logConstants.INFO,
						id + ':started taking action for DISCARD_TOKEN'
					);
					isPrimaryTokenChallenge
						? await TokenManager.getInstance().deletePrimaryToken(
								referenceUri,
								authChallenge.getAuthDomain()
						  )
						: await TokenManager.getInstance().deleteSecondaryTokensForRealm(
								authChallenge.getRealm(),
								requestUrl,
								referenceUri
						  );
					Logger.getInstance().write(
						logConstants.INFO,
						id + ':completed taking action for DISCARD_TOKEN'
					);
					break;
				case AuthChallengeConstants.DESTROY_TOKEN_FAMILY: // delete all tokens for that store
					Logger.getInstance().write(
						logConstants.INFO,
						id + ':started taking action for DESTROY_TOKEN_FAMILY' + referenceUri
					);
					await TokenManager.getInstance().deleteTokensForStore(
						referenceUri,
						authChallenge.getAuthDomain()
					);
					const storeConfigs = AuthMan.getInstance().getStoreConfiguration();
					const oidcTokenTableKey: OIDCTokensTableKey = {
						referenceUri: referenceUri,
						authDomain: authChallenge.getAuthDomain(),
					};
					const oidcEndPointTableKey: OIDCEndpointsTableKey = {
						serviceURL: storeConfigs[referenceUri].tokenServiceURL,
						authDomain: authChallenge.getAuthDomain(),
						referenceUri: referenceUri,
					};
					await OIDC.getInstance().revokeRefreshToken(
						oidcEndPointTableKey,
						oidcTokenTableKey,
						storeConfigs[referenceUri].oidcConfiguration,
						transactionId
					);
					await OIDCEndpointsManager.getInstance().deleteOIDCEndpoints(
						oidcEndPointTableKey
					);
					await OIDCTokenManager.getInstance().deleteOIDCTokens(oidcTokenTableKey);
					Logger.getInstance().write(
						logConstants.INFO,
						id + ':completed taking action for DESTROY_TOKEN_FAMILY' + referenceUri
					);
					break;
				case AuthChallengeConstants.UNKNOWN:
				case AuthChallengeConstants.REASON_MISSING:
				default:
					SentryLogger.getInstance().addBreadCrumb({
						type: 'debug',
						category: 'auth',
						message: `Received unknown auth challenge type: ${authChallengeType}`,
						level: Sentry.Severity.Info,
					});
					Logger.getInstance().write(
						logConstants.INFO,
						id +
							':' +
							authErrorString.DSAUTH_UNKNOWN_AUTH_CHALLENGE_TYPE +
							':' +
							authChallenge.getReason()
					);
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error taking action for auth challenge' + e.message
			);
		}
	}

	getCitrixTransactionIdFromHeaders(headers: HeadersJson): string {
		const transactionId = HeaderParser.getInstance().extractHeaderValue(
			headers,
			headerConstants.TRANSACTION_ID
		);
		return transactionId;
	}

	mergeStoreConfigurations(
		primaryStoreConfig: SingleStoreConfiguration,
		secondaryStoreConfig: SingleStoreConfiguration
	): SingleStoreConfiguration {
		if (!primaryStoreConfig) {
			return secondaryStoreConfig;
		}

		if (!secondaryStoreConfig) {
			return primaryStoreConfig;
		}

		const mergedStoreConfig = { ...primaryStoreConfig };
		mergedStoreConfig.tokenServiceURL ??= secondaryStoreConfig.tokenServiceURL;
		mergedStoreConfig.oidcConfiguration ??= secondaryStoreConfig.oidcConfiguration;
		mergedStoreConfig.authDomain ??= secondaryStoreConfig.authDomain;
		mergedStoreConfig.acrValues ??= secondaryStoreConfig.acrValues;
		mergedStoreConfig.promptLoginEnabled ??= secondaryStoreConfig.promptLoginEnabled;
		mergedStoreConfig.storeType ??= secondaryStoreConfig.storeType;
		mergedStoreConfig.defaultGatewayLocation ??=
			secondaryStoreConfig.defaultGatewayLocation;
		mergedStoreConfig.networkLocation ??= secondaryStoreConfig.networkLocation;
		mergedStoreConfig.storeName ??= secondaryStoreConfig.storeName;
		mergedStoreConfig.beacon ??= secondaryStoreConfig.beacon;
		mergedStoreConfig.secondaryTokenWhitelistUrls ??=
			secondaryStoreConfig.secondaryTokenWhitelistUrls;

		return mergedStoreConfig;
	}
}
